import React, { forwardRef } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MaskedInput from "react-text-mask";
import { benefitsOptions } from "../data/benefitsOptions";

const ClosePeriodMask = forwardRef(function ClosePeriodMask(props, ref) {
  return (
    <MaskedInput
      {...props}
      ref={ref.inputElement}
      mask={[/\d/, /\d/, "/", /\d/, /\d/]}
      guide={false}
      keepCharPositions={false}
      placeholderChar={" "}
      showMask
    />
  );
});

export default function AgreementFieldset({ index, disabled, onRemove, canRemove }) {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const ns = `agreements.${index}`;
  const e = errors.agreements?.[index] ?? {};

  const payrollDeduction = useWatch({ control, name: `${ns}.payrollDeduction` });

  return (
    <Box
      component="fieldset"
      sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2, mb: 1, "& legend": { px: 1 } }}
    >
      <Box component="legend" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "13px", color: "text.secondary" }}>
          Convênio {index + 1}
        </Typography>
        {canRemove && !disabled && (
          <IconButton onClick={onRemove} color="error" size="small">
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <Controller
            name={`${ns}.description`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={disabled}
                label="Descrição"
                size="small"
                fullWidth
                error={!!e.description}
                helperText={e.description?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name={`${ns}.calculationPeriod`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={disabled}
                label="Período de cálculo (ex: 05/20)"
                size="small"
                fullWidth
                InputProps={{ inputComponent: ClosePeriodMask }}
                error={!!e.calculationPeriod}
                helperText={e.calculationPeriod?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name={`${ns}.infoFormat`}
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!e.infoFormat}>
                <InputLabel id={`agreement-${index}-info-format-label`}>Formato das informações</InputLabel>
                <Select
                  {...field}
                  disabled={disabled}
                  labelId={`agreement-${index}-info-format-label`}
                  label="Formato das informações"
                >
                  {benefitsOptions.pharmacyInfoFormat.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{e.infoFormat?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name={`${ns}.reportDeadline`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={disabled}
                label="Prazo de envio do relatório (ex: 05/20)"
                size="small"
                fullWidth
                InputProps={{ inputComponent: ClosePeriodMask }}
                error={!!e.reportDeadline}
                helperText={e.reportDeadline?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={payrollDeduction === "5" ? 6 : 12}>
              <Controller
                name={`${ns}.payrollDeduction`}
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" error={!!e.payrollDeduction}>
                    <InputLabel id={`agreement-${index}-payroll-deduction-label`}>Desconto em folha</InputLabel>
                    <Select
                      {...field}
                      disabled={disabled}
                      labelId={`agreement-${index}-payroll-deduction-label`}
                      label="Desconto em folha"
                    >
                      {benefitsOptions.pharmacyPayrollDeduction.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{e.payrollDeduction?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            {payrollDeduction === "5" && (
              <Grid item xs={12} md={6}>
                <Controller
                  name={`${ns}.payrollDeductionOther`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled={disabled}
                      label="Descreva a Regra de Negócio"
                      size="small"
                      fullWidth
                      error={!!e.payrollDeductionOther}
                      helperText={e.payrollDeductionOther?.message}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name={`${ns}.terminationDeduction`}
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!e.terminationDeduction}>
                <InputLabel id={`agreement-${index}-termination-deduction-label`}>Desconto na rescisão</InputLabel>
                <Select
                  {...field}
                  disabled={disabled}
                  labelId={`agreement-${index}-termination-deduction-label`}
                  label="Desconto na rescisão"
                >
                  {benefitsOptions.pharmacyTerminationDeduction.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{e.terminationDeduction?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name={`${ns}.unusedBenefitDeduction`}
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!e.unusedBenefitDeduction}>
                <InputLabel id={`agreement-${index}-unused-benefit-deduction-label`}>Desconto benefício não utilizado</InputLabel>
                <Select
                  {...field}
                  disabled={disabled}
                  labelId={`agreement-${index}-unused-benefit-deduction-label`}
                  label="Desconto benefício não utilizado"
                >
                  {benefitsOptions.pharmacyUnusedBenefitDeduction.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{e.unusedBenefitDeduction?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
